import i18n from '~/i18n'

import localstorageConstants from './localstorage-manipulation'

const getValidLanguage = (): string => {
  const language = i18n.language || 'pt-BR'

  try {
    // Testa se o código de idioma é válido
    new Intl.NumberFormat(language)
    return language // Se for válido, retorna o mesmo código
  } catch (error) {
    // Se houver erro, retorna um código de idioma padrão
    console.warn(`Idioma inválido: ${language}. Usando 'pt-BR' como padrão.`)
    return 'pt-BR'
  }
}

/**
 * Formata o valor tipo number para um formato com pontos
 * @param {number} value Valor que será formatdo
 * @returns {string} Valor formatado com pontos
 * @example
 * const number = 23011987
 * formatNumberWithDots(number)
 * Return: "23.011.987"
 */
export const formatNumberWithDots = (value: string | number) => {
  const toNumber = Number(value)

  return Number.isNaN(toNumber) ? '-' : toNumber.toLocaleString('pt-br')
}

export const brlToFloat = value => {
  if (typeof value === 'string') {
    return parseFloat(value.replace('.', '').replace(',', '.'))
  }

  return value
}

export const formatMoney = (
  value,
  returnNumber = false,
  fallbackMessage = '-'
) => {
  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) return fallbackMessage

  const valueNumber = numericValue.toFixed(3)

  const language = getValidLanguage()

  let currency = 'BRL'

  const storageValue = localStorage.getItem(localstorageConstants.APP_INFO)

  if (storageValue) {
    const appInfo = JSON.parse(storageValue)

    currency = appInfo?.currencyCode
  }

  const fractionDigits = currency === 'COP' ? 0 : 2

  const formatter = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })

  return formatter.format(valueNumber)
}

export const formatMoneyV2 = ({
  value,
  fallbackMessage = '-'
}: {
  value?: string | number | null
  fallbackMessage?: string
}) => {
  const numericValue = Number(value)

  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    Number.isNaN(numericValue)
  ) {
    return fallbackMessage
  }

  const valueNumber = Number(value).toFixed(3)

  const language = getValidLanguage()

  let currency = 'BRL'

  const storageValue = localStorage.getItem(localstorageConstants.APP_INFO)

  if (storageValue) {
    const appInfo = JSON.parse(storageValue)

    currency = appInfo?.currencyCode
  }

  const fractionDigits = currency === 'COP' ? 0 : 2

  const formatter = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })

  return formatter.format(Number(valueNumber))
}

/**
 * Trata o digitação dos inputs de dinheiro e faz começa-los pelos centavos.
 *
 * @param {number} value valor retornado pelo inpit
 */
export const formatMoneyInputs = (value: number) => {
  const language = getValidLanguage()
  let currency = 'BRL'
  let currencyUseCents = true

  const storageValue = localStorage.getItem(localstorageConstants.APP_INFO)

  if (storageValue) {
    const appInfo: AppInfoDTOData = JSON.parse(storageValue)

    currency = appInfo?.currencyCode
    currencyUseCents = appInfo?.currencyUseCents
  }

  const fractionDigits = currencyUseCents ? 2 : 0

  const formatter = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })

  if (!Number(value)) return formatter.format(0)

  const valueToFormat = currencyUseCents ? value / 100 : value

  const amount = formatter.format(valueToFormat)

  return `${amount}`
}

/**
 * Igual ao formatMoneyInputs, mas sem o símbolo de moeda.
 * Útil para inputs que já exibem o símbolo via htmlIcon.
 *
 * @param {number} value valor retornado pelo input
 */
export const formatMoneyInputsWithoutSymbol = (value: number) => {
  const language = getValidLanguage()
  let currencyUseCents = true
  let currency = 'BRL'

  const storageValue = localStorage.getItem(localstorageConstants.APP_INFO)

  if (storageValue) {
    const appInfo: AppInfoDTOData = JSON.parse(storageValue)

    currency = appInfo?.currencyCode
    currencyUseCents = appInfo?.currencyUseCents
  }

  const fractionDigits = !currencyUseCents && currency === 'COP' ? 0 : 2

  const formatter = new Intl.NumberFormat(language, {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })

  const valueToFormat = currencyUseCents ? value / 100 : value

  return formatter.format(Number(valueToFormat) || 0)
}

/**
 * Formata um valor monetário sem o símbolo de moeda.
 *
 * @param value Valor numérico a ser formatado
 * @param fallbackMessage Mensagem de fallback caso o valor seja inválido
 * @returns Valor formatado sem símbolo (ex: "25,00" ou "1.234,56")
 *
 * @example
 * formatMoneyWithoutSymbol({ value: 25 })       // "25,00"
 * formatMoneyWithoutSymbol({ value: 1234.56 })  // "1.234,56"
 */
export const formatMoneyWithoutSymbol = ({
  value,
  fallbackMessage = '-'
}: {
  value?: string | number | null
  fallbackMessage?: string
}) => {
  const numericValue = Number(value)

  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    Number.isNaN(numericValue)
  ) {
    return fallbackMessage
  }

  const language = getValidLanguage()

  let currency = 'BRL'

  const storageValue = localStorage.getItem(localstorageConstants.APP_INFO)

  if (storageValue) {
    const appInfo = JSON.parse(storageValue)
    currency = appInfo?.currencyCode
  }

  const fractionDigits = currency === 'COP' ? 0 : 2

  const formatter = new Intl.NumberFormat(language, {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })

  return formatter.format(numericValue)
}

/**
 * Remove a formatação do dinheiro.
 * Exemplo: R$ 23.202,20 = 232020.20
 *
 * @param {string} value valor formato em R$
 */

export const unformatMoney = (value: string | number) => {
  if (!Number.isNaN(Number(value))) return value

  const stringValue = `${value}`

  const formattedValue =
    stringValue
      .replace('R$', '')
      .replace('$', '')
      .replace('COP', '')
      .replace('PEN', '')
      .replace('S/', '')
      .replace(/\./g, '')
      .replace(/,/g, '.')
      .trim() || null

  return value ? formattedValue : 0
}

export const formatPercent = (value, digits = 2) => {
  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) return '-'

  const language = getValidLanguage()

  const formatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })

  return `${formatter.format(numericValue)}%`
}

export const percentage = (partialValue, totalValue) =>
  (100 * partialValue) / totalValue

/**
 * CPF
 */

export const formatCPF = (cpf: string) => {
  cpf = cpf.replace(/[^\d]/g, '')

  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

/**
 * Number with k or m
 */

export function formatNumberWithK({
  number = 0,
  fixexThousand = 0,
  fixedMillion = 0
}) {
  if (number >= 1000000) {
    const formatted = (number / 1000000).toFixed(fixedMillion)
    return `${formatted}M`
  }

  if (number >= 1000) {
    const formatted = (number / 1000).toFixed(fixexThousand)
    return `${formatted}k`
  }

  return number?.toString() || '-'
}

/**
 * Converte o valor de input de dinheiro do formato de centavos (string) para número em reais.
 *
 * ⚠️ Esta função é útil para campos onde os valores são digitados como centavos,
 * e você precisa convertê-los para reais ao salvar, dividindo por 100.
 *
 * @param {string} value Valor do input monetário em centavos (ex: "2500" representa R$25,00)
 * @returns {number} Valor em reais com casas decimais (ex: 25)
 *
 * @example
 * convertInputMoney("1500") // Retorna: 15
 * convertInputMoney()       // Retorna: 0
 */
export const convertInputMoney = (value?: string): number => {
  if (!value) return 0
  return Number(value) / 100
}

/**
 * Formata o valor de entrada do input de dinheiro para o número correto.
 * O Input de dinheiro retorna um tipo InputMoneyValuesOnChange,
 * que contém o valor formatado e o valor bruto.
 * Esta função converte o valor bruto para o formato com centavos.
 *
 * @param value
 * @param lengthCentsCurrency
 * @returns number
 */
export function formatInputMoneyToNumber(
  value: InputMoneyValuesOnChange,
  lengthCentsCurrency?: number
) {
  const lengthCents = lengthCentsCurrency || 2

  const valueRaw = value?.floatValue

  const convertedValue =
    lengthCents > 0 ? Number(valueRaw) / 100 : Number(valueRaw)

  return valueRaw ? convertedValue : null
}

/**
 * Verifica se o valor é um número positivo.
 * @param value Valor a ser verificado.
 * @returns true se o valor for um número positivo, false caso contrário.
 */

/**
 * Retorna o símbolo da moeda configurada no APP_INFO,
 * compatível com o símbolo exibido pelo Intl.NumberFormat com style: 'currency'.
 *
 * @returns {string} Símbolo da moeda (ex: "R$", "$", "S/")
 */
export const getCurrencySymbol = (): string => {
  const language = getValidLanguage()
  let currency = 'BRL'

  const storageValue = localStorage.getItem(localstorageConstants.APP_INFO)
  if (storageValue) {
    const appInfo = JSON.parse(storageValue)
    currency = appInfo?.currencyCode || 'BRL'
  }

  const parts = new Intl.NumberFormat(language, {
    style: 'currency',
    currency
  }).formatToParts(0)

  return parts.find(p => p.type === 'currency')?.value ?? currency
}

export const isPositiveNumber = (
  value: number | string | null | undefined
): value is number => {
  const numberValue = typeof value === 'string' ? Number(value) : value
  return numberValue != null && numberValue > 0
}
